import React from 'react';

import { type History } from 'history';
import { Router } from 'react-router-dom';

import FabricAnalyticsListeners from '@atlaskit/analytics-listeners';
import { isFedRamp } from '@atlaskit/atlassian-context';
import { useAnalyticsEvents } from '@atlaskit/teams-app-internal-analytics';
import { useTeamsClientSetupNext } from '@atlaskit/teams-client';
import { useTeamsSentryClientSetup } from '@atlaskit/teams-client/sentry';
import Heartbeat from '@atlassian/heartbeat';
import {
	AnalyticsProvider,
	DialogsProvider,
	I18nProvider,
	redirectCentralLogin,
	usePrincipalUser,
	useSetupAnalyticsClient,
} from '@atlassian/ptc-common';

import ErrorBoundary from '../../common/ui/ErrorBoundary';
import { useOrgIdState } from '../../controllers/org-id';
import { useSiteStandaloneApp } from '../../controllers/site-standalone-app';
import messagesConfig from '../../i18n/messagesConfig';

import Flags from './Flags';
import AppRoutes from './Routes';
import { redirectToHome } from './utils';

export interface AppContainerProps {
	history: History;
	appName: string;
}

function AppContainer(props: AppContainerProps): React.JSX.Element | null {
	const { appName, history } = props;

	const [{ cloudId }] = useSiteStandaloneApp();
	const [{ orgId }] = useOrgIdState();
	const [{ email, principalUserId }] = usePrincipalUser();
	useTeamsSentryClientSetup(true, { appName });
	useTeamsClientSetupNext({
		cloudId,
		orgId,
		principalUserId,
	});

	const analyticsClient = useSetupAnalyticsClient(cloudId, 'site-directory');
	const { fireEvent } = useAnalyticsEvents();

	if (!analyticsClient) {
		return null;
	}

	const isRedirectEscapeHatchEnabled = window.location.search.includes('disable-home-redirect');
	const isLocalhost = ['localhost', '127.0.0.1'].includes(window.location.hostname);
	// agent/:agentId/embed or team/:teamId/embed
	const isEmbedRoute =
		(window.location.pathname.includes('/agent/') || window.location.pathname.includes('/team/')) &&
		window.location.pathname.includes('/embed');

	if (
		!isEmbedRoute &&
		!isRedirectEscapeHatchEnabled &&
		!isLocalhost &&
		// We serve Standalone in Fedramp
		!isFedRamp()
	) {
		redirectToHome({
			email,
			orgId,
			cloudId,
		});
		return null;
	}

	return (
		<I18nProvider messagesConfig={messagesConfig}>
			<FabricAnalyticsListeners
				// @ts-ignore Analytics web client types mismatch
				client={analyticsClient.getAnalyticsWebClient()}
			>
				<ErrorBoundary appName={appName} analytics={analyticsClient} fireEvent={fireEvent}>
					<AnalyticsProvider analytics={analyticsClient}>
						<DialogsProvider>
							<Router history={history}>
								<AppRoutes />
							</Router>
							<Flags />
							<Heartbeat onAuthenticationFailed={redirectCentralLogin} />
						</DialogsProvider>
					</AnalyticsProvider>
				</ErrorBoundary>
			</FabricAnalyticsListeners>
		</I18nProvider>
	);
}

export default AppContainer;
